import { faSave } from "@fortawesome/free-solid-svg-icons";
import React, { type FC, useEffect, useState } from "react";
import { useApi } from "../api/APIContext";
import { useApiCall } from "../api/api";
import type { Company, Project } from "../api/dto";
import type { Inspection } from "../api/dto/Inspection";
import { Input } from "../components/form/Input";
import { Select } from "../components/form/Select";
import { Toggle } from "../components/form/Toggle";
import { ModalFooter } from "../components/layout/ModalProvider";
import { useRefresh } from "../components/RefreshController";

export const EditInspectionModal: FC<{ inspection: Inspection }> = (props) => {
  const { companies, projects, inspections } = useApi();
  const { addOrUpdateInspection } = useApiCall();
  const reload = useRefresh();

  // Company
  const [company, setCompany] = useState<Company["id"]>(props.inspection.companyId);
  const companyOptions = Object.fromEntries([
    ["-", "Selecteer bedrijf"],
    ...companies.filter((c) => !c.isArchived).map((c) => [c.id, c.name]),
  ]);

  // Project
  const [project, setProject] = useState<Project["id"]>(props.inspection.projectId);
  const projectOptions = Object.fromEntries([
    ["-", "Selecteer project"],
    ...projects
      .filter((p) => !p.isArchived)
      .filter((p) => p.companyId === company)
      .map((p) => [p.id, p.name]),
  ]);
  useEffect(() => {
    if (!Object.keys(projectOptions).includes(project)) {
      setProject("-");
    }
  }, [project, projectOptions]);

  // Inspection name
  const [name, setName] = useState<string>(props.inspection.name ?? "");
  const inspectionNames = Array.from(
    new Set(inspections.filter((e) => e.id !== props.inspection.id).map((e) => e.name ?? "-")),
  );
  const isUnique = !inspectionNames.includes(name);

  // Inspection start at
  const [startAt, setStartAt] = useState<Date | null>(props.inspection.startedAt);
  const [finishedAt, setFinishedAt] = useState<Date | null>(props.inspection.finishedAt);

  const confirm = async () => {
    await addOrUpdateInspection(props.inspection.id, {
      company_id: company,
      project_id: project,
      question_set_id: props.inspection.questionSetId,
      assessor_id: props.inspection.assessorId,
      started_at: startAt?.toISOString() ?? null,
      finished_at: startAt === null ? null : (finishedAt?.toISOString() ?? null),
      name: name,
    });
    reload();
  };

  return (
    <>
      <div className={"grid grid-cols-1 lg:grid-cols-2 gap-3"}>
        <div className={"grid grid-cols-1 gap-3"}>
          <Select
            options={companyOptions}
            value={company}
            onChange={setCompany}
            label={"Bedrijf"}
          />
          <Select
            options={projectOptions}
            value={project}
            onChange={setProject}
            label={"Project"}
          />
          <Input type={"text"} label={"Inspectie naam"} value={name} onChange={setName} />
          {isUnique ? null : (
            <p className={"text-red-500"}>Deze inspectionennaam is al in gebruik.</p>
          )}
        </div>
        <div className={"grid grid-cols-1 gap-3"}>
          <Toggle
            label={"Inspectie gestart?"}
            value={startAt !== null}
            onChange={(newValue) =>
              !newValue
                ? setStartAt(null)
                : setStartAt(
                    props.inspection.startedAt ?? props.inspection.inspectionAt ?? new Date(),
                  )
            }
          />
          {startAt !== null ? (
            <>
              <Input type={"time"} label={"Starttijd"} value={startAt} onChange={setStartAt} />
              <Toggle
                label={"Inspectie afgerond?"}
                value={finishedAt !== null}
                onChange={(newValue) =>
                  !newValue
                    ? setFinishedAt(null)
                    : setFinishedAt(
                        props.inspection.finishedAt ?? props.inspection.inspectionAt ?? new Date(),
                      )
                }
              />
              {finishedAt !== null ? (
                <>
                  <Input
                    type={"time"}
                    label={"Eindtijd"}
                    value={finishedAt}
                    onChange={setFinishedAt}
                  />
                </>
              ) : (
                <></>
              )}
            </>
          ) : (
            <></>
          )}
        </div>
      </div>
      <ModalFooter
        icon={faSave}
        text={"Opslaan"}
        onSubmit={confirm}
        disabled={company === "-" || project === "-" || !isUnique}
      />
    </>
  );
};
